import type { ComponentChildren, ComponentProps, JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Input } from '../Input/Input';
import styles from './AutocompleteInput.module.css';

export interface AutocompleteInputItem {
  key: string;
  value: string;
  content: ComponentChildren;
  updateInputOnSelect?: boolean;
  onSelect?: () => void;
}

interface AutocompleteInputProps extends Omit<
  ComponentProps<'input'>,
  'className' | 'value' | 'onInput' | 'onChange'
> {
  label?: string;
  value: string;
  items?: AutocompleteInputItem[];
  open?: boolean;
  onValueChange?: (value: string) => void;
  onSelectItem?: (item: AutocompleteInputItem) => void;
}

export function AutocompleteInput({
  label,
  value,
  items = [],
  open,
  onValueChange,
  onSelectItem,
  onFocus,
  onBlur,
  ...props
}: AutocompleteInputProps) {
  const [isOpenInternal, setIsOpenInternal] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);

  const isOpenControlled = typeof open === 'boolean';
  const isOpen = isOpenControlled ? open : isOpenInternal;

  const setOpenState = (nextValue: boolean) => {
    if (!isOpenControlled) {
      setIsOpenInternal(nextValue);
    }
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<Array<HTMLLIElement | null>>([]);
  const listboxIdRef = useRef(
    `autocomplete-listbox-${Math.random().toString(36).slice(2, 10)}`,
  );

  const hasItems = items.length > 0;
  const activeDescendantId =
    highlightedIndex === null
      ? undefined
      : `${listboxIdRef.current}-option-${highlightedIndex}`;

  useEffect(() => {
    const handlePointerDown = (event: globalThis.MouseEvent) => {
      const targetNode = event.target as Node | null;
      if (!targetNode) {
        return;
      }

      if (!containerRef.current?.contains(targetNode)) {
        setOpenState(false);
        setHighlightedIndex(null);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, []);

  useEffect(() => {
    if (highlightedIndex !== null && highlightedIndex >= items.length) {
      setHighlightedIndex(null);
    }
  }, [highlightedIndex, items.length]);

  useEffect(() => {
    if (!isOpen || highlightedIndex === null) {
      return;
    }

    const highlightedOption = optionRefs.current[highlightedIndex];
    if (typeof highlightedOption?.scrollIntoView === 'function') {
      highlightedOption.scrollIntoView({
        block: 'nearest',
      });
    }
  }, [highlightedIndex, isOpen]);

  const selectItem = (item: AutocompleteInputItem) => {
    if (item.updateInputOnSelect !== false) {
      onValueChange?.(item.value);
    }

    item.onSelect?.();
    onSelectItem?.(item);
    setOpenState(false);
    setHighlightedIndex(null);
  };

  const handleInput = (event: JSX.TargetedEvent<HTMLInputElement, Event>) => {
    onValueChange?.(event.currentTarget.value);
    setOpenState(true);
    setHighlightedIndex(null);
  };

  const handleKeyDown = (
    event: JSX.TargetedKeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Escape') {
      setOpenState(false);
      setHighlightedIndex(null);
      return;
    }

    if (!hasItems) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpenState(true);
      setHighlightedIndex((prev) => {
        if (prev === null) {
          return 0;
        }

        return (prev + 1) % items.length;
      });
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setOpenState(true);
      setHighlightedIndex((prev) => {
        if (prev === null) {
          return items.length - 1;
        }

        return (prev - 1 + items.length) % items.length;
      });
    }

    if (event.key === 'Enter' && isOpen && highlightedIndex !== null) {
      event.preventDefault();
      selectItem(items[highlightedIndex]);
    }
  };

  return (
    <div ref={containerRef} className={styles.container}>
      <Input
        required
        {...props}
        label={label}
        value={value}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onFocus={(event) => {
          onFocus?.(event);
          if (items.length > 0) {
            setOpenState(true);
          }
        }}
        onBlur={(event) => {
          onBlur?.(event);
          setOpenState(false);
          setHighlightedIndex(null);
        }}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={isOpen && hasItems}
        aria-controls={hasItems ? listboxIdRef.current : undefined}
        aria-activedescendant={activeDescendantId}
      />

      {isOpen && hasItems && (
        <ul
          className={styles.dropdown}
          role="listbox"
          id={listboxIdRef.current}
        >
          {items.map((item, index) => {
            const isHighlighted = highlightedIndex === index;

            return (
              <li
                key={item.key}
                ref={(el) => {
                  optionRefs.current[index] = el;
                }}
                id={`${listboxIdRef.current}-option-${index}`}
                role="option"
                aria-selected={isHighlighted}
                className={`${styles.dropdownItem} ${isHighlighted ? styles.dropdownItemActive : ''}`.trim()}
                // TODO: Collapse pointer and click selection into one path so
                // that mouse selection cannot trigger item side effects twice.
                onPointerDown={(event) => {
                  event.preventDefault();
                  selectItem(item);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
                onClick={() => selectItem(item)}
              >
                {/* SVG goes here */}
                {item.content}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
